/**
 * LiveActivityWidget.tsx
 * Horizontal auto-scrolling marquee of live + recent exam activity.
 * Cards slide right→left slowly with blurred real avatars.
 * Fade edges + navigation arrows for manual scrolling.
 */

import { useRef, useState, useEffect } from 'react';
import { useLiveActivity, type LiveSession } from '../hooks/useLiveActivity';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Users } from 'lucide-react';
import { trackLiveWidgetImpression, trackLiveWidgetClick } from '../telemetry/googleAnalytics';
import { timeAgo } from '../lib/utils';

/** Deterministic pastel color from email string (fallback for no avatar) */
function avatarColor(email: string): string {
  let hash = 0;
  for (let i = 0; i < email.length; i++) {
    hash = email.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 40%, 65%)`;
}

interface LiveActivityWidgetProps {
  enabled?: boolean;
  onExamClick?: (examId: string) => void;
  maxEntries?: number;
}

export function LiveActivityWidget({
  enabled = true,
  onExamClick,
  maxEntries = 20,
}: LiveActivityWidgetProps) {
  const activity = useLiveActivity({ enabled, intervalMs: 30_000 });
  const [isPaused, setIsPaused] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);
  const impressionFiredRef = useRef(false);
  const pendingTransitionRef = useRef<(() => void) | null>(null);

  // Track widget impression once (must be before early return to preserve hook order)
  const allSessions = activity?.allSessions ?? [];
  const totalCount = activity?.totalCount ?? 0;
  useEffect(() => {
    if (!impressionFiredRef.current && allSessions.length > 0) {
      impressionFiredRef.current = true;
      trackLiveWidgetImpression(totalCount);
    }
  }, [allSessions.length, totalCount]);

  if (!enabled || !activity?.hasData) return null;

  const { raw } = activity;
  const displaySessions = allSessions.slice(0, maxEntries);
  const hasActive = raw.totalActive > 0;

  // Slow speed: ~6s per card, minimum 30s
  const duration = Math.max(displaySessions.length * 6, 30);

  const handleManualScroll = (direction: 'left' | 'right') => {
    const el = trackRef.current;
    if (!el) return;
    // Clean up any pending transition listener from rapid clicks
    if (pendingTransitionRef.current) {
      el.removeEventListener('transitionend', pendingTransitionRef.current);
      pendingTransitionRef.current = null;
    }
    // Read current position from computed transform
    const style = getComputedStyle(el);
    const matrix = new DOMMatrix(style.transform);
    const currentX = matrix.m41;
    const totalWidth = el.scrollWidth / 2;
    const shift = direction === 'right' ? -268 : 268;
    let targetX = currentX + shift;
    if (targetX < -totalWidth) targetX += totalWidth;
    if (targetX > 0) targetX -= totalWidth;
    // Stop CSS animation, use CSS transition to slide smoothly
    el.style.animation = 'none';
    el.style.transform = `translateX(${currentX}px)`;
    el.style.transition = 'none';
    void el.offsetHeight; // force reflow
    el.style.transition = 'transform 0.4s ease-out';
    el.style.transform = `translateX(${targetX}px)`;
    // After transition ends, resume CSS animation from new position
    const onEnd = () => {
      el.removeEventListener('transitionend', onEnd);
      pendingTransitionRef.current = null;
      el.style.transition = 'none';
      el.style.transform = '';
      el.style.animation = '';
      const progress = Math.abs(targetX) / totalWidth;
      el.style.animationDelay = `-${progress * duration}s`;
      el.style.animationDuration = `${duration}s`;
      el.style.animationPlayState = 'paused';
    };
    pendingTransitionRef.current = onEnd;
    el.addEventListener('transitionend', onEnd);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className='mb-6'
    >
      {/* Header */}
      <div className='flex items-center gap-2 mb-3'>
        <div className='relative'>
          <div className='w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center'>
            <Users className='w-3.5 h-3.5 text-primary' />
          </div>
          {hasActive && (
            <span className='absolute -top-0.5 -right-0.5 flex h-2 w-2'>
              <span className='animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75' />
              <span className='relative inline-flex rounded-full h-2 w-2 bg-green-500' />
            </span>
          )}
        </div>
        <span className='text-sm font-medium text-gray-700 dark:text-gray-200'>
          {hasActive ? `${raw.totalActive} học sinh đang luyện tập` : 'Hoạt động luyện tập'}
        </span>
        <span className='text-xs text-gray-400 dark:text-gray-500'>
          · {totalCount} lượt thi trong 7 ngày
        </span>
      </div>

      {/* Marquee container */}
      <div
        className='relative overflow-hidden group'
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {/* Left nav button */}
        <button
          onClick={() => handleManualScroll('left')}
          className='absolute left-0 top-0 bottom-0 w-10 z-20 flex items-center justify-start pl-1 bg-gradient-to-r from-white/90 dark:from-gray-950/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer'
          aria-label='Xem trước'
        >
          <div className='w-7 h-7 rounded-full bg-white dark:bg-gray-800 shadow-md border border-gray-200 dark:border-gray-600 flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors'>
            <ChevronLeft className='w-4 h-4 text-gray-600 dark:text-gray-300' />
          </div>
        </button>

        {/* Right nav button */}
        <button
          onClick={() => handleManualScroll('right')}
          className='absolute right-0 top-0 bottom-0 w-10 z-20 flex items-center justify-end pr-1 bg-gradient-to-l from-white/90 dark:from-gray-950/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer'
          aria-label='Xem thêm'
        >
          <div className='w-7 h-7 rounded-full bg-white dark:bg-gray-800 shadow-md border border-gray-200 dark:border-gray-600 flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors'>
            <ChevronRight className='w-4 h-4 text-gray-600 dark:text-gray-300' />
          </div>
        </button>

        {/* Fade edges */}
        <div className='absolute left-0 top-0 bottom-0 w-6 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none' />
        <div className='absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none' />

        {/* Two identical tracks side by side for seamless loop */}
        <div
          ref={trackRef}
          className='flex marquee-live-track'
          style={{
            animationPlayState: isPaused ? 'paused' : 'running',
            animationDuration: `${duration}s`,
          }}
        >
          {[0, 1].map(copy => (
            <div key={copy} className='flex gap-3 shrink-0 pr-3'>
              {displaySessions.map((session, i) => (
                <SessionCard
                  key={`${copy}-${session.maskedEmail}-${session.type}-${i}`}
                  session={session}
                  onClick={() => {
                    trackLiveWidgetClick(session.examId, session.examTitle);
                    onExamClick?.(session.examId);
                  }}
                />
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Marquee CSS */}
      <style>{`
        @keyframes marquee-live {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .marquee-live-track {
          animation: marquee-live linear infinite;
          width: max-content;
        }
      `}</style>
    </motion.div>
  );
}

function SessionCard({ session, onClick }: { session: LiveSession; onClick: () => void }) {
  const isActive = session.type === 'ACTIVE';
  const time = isActive
    ? session.startedAt
      ? timeAgo(session.startedAt, true)
      : ''
    : session.completedAt
      ? timeAgo(session.completedAt, true)
      : '';

  const fallbackBg = avatarColor(session.maskedEmail);
  const initial = session.maskedEmail.charAt(0).toUpperCase();
  const hasAvatar = !!session.avatar;

  return (
    <div
      onClick={onClick}
      className='flex-shrink-0 w-64 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 px-3.5 py-3 cursor-pointer hover:border-primary/40 hover:shadow-md transition-all group/card'
    >
      <div className='flex items-center gap-3'>
        {/* Avatar — real image blurred, or fallback pastel circle */}
        <div
          className='w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden relative'
          style={hasAvatar ? undefined : { backgroundColor: fallbackBg }}
        >
          {hasAvatar ? (
            <>
              <img
                src={session.avatar!}
                alt=''
                className='w-full h-full object-cover blur-[6px] scale-110'
                loading='lazy'
              />
              <div className='absolute inset-0 bg-black/10 rounded-full' />
            </>
          ) : (
            <>
              <span className='text-white/70 text-sm font-bold blur-[2px]'>{initial}</span>
              <div className='absolute inset-0 bg-white/10 rounded-full' />
            </>
          )}
          {/* Active indicator dot */}
          {isActive && (
            <span className='absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white dark:border-gray-900' />
          )}
        </div>

        {/* Content — wider for full exam name */}
        <div className='min-w-0 flex-1'>
          <div className='flex items-center gap-1.5 mb-0.5'>
            <span className='text-xs font-semibold text-gray-700 dark:text-gray-200 truncate'>
              {session.maskedEmail}
            </span>
            <span className='text-[10px] text-gray-400'>{time}</span>
          </div>
          <p className='text-[12px] text-gray-700 dark:text-gray-300 leading-tight'>
            {isActive ? (
              <>
                đang làm{' '}
                <span className='font-medium text-gray-700 dark:text-gray-200'>
                  {session.examTitle}
                </span>
              </>
            ) : (
              <>
                hoàn thành{' '}
                <span className='font-medium text-gray-700 dark:text-gray-200'>
                  {session.examTitle}
                </span>
                {session.score != null && (
                  <span className='ml-1 text-emerald-600 dark:text-emerald-400 font-bold'>
                    — {session.score}/{session.totalScore} điểm
                  </span>
                )}
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
